import { useExtractionJobs } from '@ctw/shared/api/ai/use-extraction-jobs';
import { useCurrentUserRole } from '@ctw/shared/api/auth-service/use-current-user-role';
import { Button } from '@ctw/shared/components/button';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { SelectInput } from '@ctw/shared/components/form/select-input';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { useOpenSourceDocument } from '@ctw/shared/content/CCDA/use-open-source-document';
import { computeExportSummary } from '@ctw/shared/content/fact-extraction/compute-export-summary';
import {
  buildReportOptions,
  mapExtractionJob,
} from '@ctw/shared/content/fact-extraction/map-extraction-job';
import { usePatientFactExtractionExportModal } from '@ctw/shared/content/fact-extraction/patient-fact-extraction-export-modal';
import {
  type PatientFact,
  PatientFactExtractionGroupedTable,
  type PatientFactGroup,
} from '@ctw/shared/content/fact-extraction/patient-fact-extraction-grouped-table';
import {
  type CitationSource,
  useCitationSources,
} from '@ctw/shared/content/fact-extraction/use-citation-sources';
import {
  pruneExcludedFieldsStorage,
  useExcludedFields,
} from '@ctw/shared/content/fact-extraction/use-excluded-fields';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pluralize from 'pluralize';
import { useEffect, useMemo, useState } from 'react';

export interface AtlasExportVisibilityParams {
  /** Whether the `ctw-atlas-export` unleash flag is on. */
  flagEnabled: boolean;
  isZusUser: boolean;
  /**
   * Role ids permitted to export, from the builder config's `atlas` settings.
   * `undefined` (key absent) and `[]` (empty) both mean "open to all builder
   * users"; only a non-empty list restricts export to the listed role ids.
   */
  allowedExportRoleIds: Array<string> | undefined;
  /** The current user's role id, or `undefined` while the role query is loading. */
  currentUserRoleId: string | undefined;
}

/**
 * Decides whether the Atlas export button should be shown.
 *
 * flagEnabled AND (isZusUser OR allowlist empty/absent OR role id in allowlist).
 */
export function isAtlasExportAllowed({
  flagEnabled,
  isZusUser,
  allowedExportRoleIds,
  currentUserRoleId,
}: AtlasExportVisibilityParams): boolean {
  if (!flagEnabled) {
    return false;
  }
  if (isZusUser) {
    return true;
  }
  if (!allowedExportRoleIds || allowedExportRoleIds.length === 0) {
    return true;
  }
  return currentUserRoleId !== undefined && allowedExportRoleIds.includes(currentUserRoleId);
}

export const PatientFactExtraction = withErrorBoundary({
  boundaryName: 'PatientFactExtraction',
  Component: () => {
    const { patient } = usePatientContext();
    const { data: jobs = [], isLoading } = useExtractionJobs(patient.UPID);

    const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);

    const reportOptions = useMemo(() => buildReportOptions(jobs), [jobs]);
    const selectedJob = useMemo(
      () => jobs.find((job) => job.id === selectedJobId) ?? jobs[0],
      [jobs, selectedJobId],
    );

    const mapped = useMemo(
      () => (selectedJob ? mapExtractionJob(selectedJob) : undefined),
      [selectedJob],
    );

    const { sourceByCitationId } = useCitationSources(selectedJob?.attributes.citations ?? []);
    const { openSourceDocumentById } = useOpenSourceDocument();
    const { openResourceDrawer } = useResourceDrawer();
    const telemetry = useTelemetry();

    const atlasExportFlagEnabled = useFeatureFlag('ctw-atlas-export');
    const { requestContext } = useCTW();
    const isZusUser = requestContext.authTokenState.userType === 'zus';
    const { atlas } = useBuilderConfig();
    const allowedExportRoleIds = atlas?.allowedExportRoleIds;
    const hasAllowlist = !!allowedExportRoleIds && allowedExportRoleIds.length > 0;

    // The user's role is only consulted when a non-empty allowlist is in play.
    // For a zus user, no allowlist, or the flag being off, export visibility is
    // already decided — so skip the role fetch entirely in those cases.
    const roleCheckNeeded = atlasExportFlagEnabled && !isZusUser && hasAllowlist;
    const { role, isLoading: isRoleLoading } = useCurrentUserRole({ enabled: roleCheckNeeded });

    const atlasExportEnabled = isAtlasExportAllowed({
      flagEnabled: atlasExportFlagEnabled,
      isZusUser,
      allowedExportRoleIds,
      currentUserRoleId: role?.id,
    });

    // We only wait on the role fetch when it's actually needed (roleCheckNeeded);
    // otherwise export visibility is already known and no spinner is shown.
    const atlasExportLoading = roleCheckNeeded && isRoleLoading;

    const { excludedIds, isExcluded, toggleExcluded } = useExcludedFields(
      patient.UPID ?? '',
      selectedJob?.id,
    );
    const openExportModal = usePatientFactExtractionExportModal();

    // Atlas jobs expire server-side; drop persisted exclusions for jobs that no
    // longer exist. Gated on the raw feature flag (not export permission) so
    // cleanup still runs for users who can't export, and on a non-empty list so
    // a failed/empty fetch can't wipe valid entries.
    useEffect(() => {
      if (atlasExportFlagEnabled && patient.UPID && jobs.length > 0) {
        pruneExcludedFieldsStorage(
          patient.UPID,
          jobs.map((job) => job.id),
        );
      }
    }, [atlasExportFlagEnabled, patient.UPID, jobs]);

    const exportSummary = computeExportSummary({
      groups: mapped?.groups ?? [],
      excludedIds,
      sourceByCitationId,
    });

    const { factGroups, sourceByFactId } = useMemo(() => {
      const sourceById = new Map<string, CitationSource>();
      const groups: Array<PatientFactGroup> = (mapped?.groups ?? []).map((group) => ({
        label: group.label,
        facts: group.facts.map((fact): PatientFact => {
          if (!fact.isDocumented) {
            return { id: fact.id, label: fact.label };
          }
          if (fact.citationIds.length === 0) {
            return { id: fact.id, label: fact.label, value: fact.value };
          }
          const openableCitationId = fact.citationIds.find((citationId) =>
            sourceByCitationId.has(citationId),
          );
          const source =
            openableCitationId !== undefined
              ? sourceByCitationId.get(openableCitationId)
              : undefined;
          if (source) {
            sourceById.set(fact.id, source);
          }
          return {
            id: fact.id,
            label: fact.label,
            value: fact.value,
            citation: { isOpenable: Boolean(source), label: source?.label },
          };
        }),
      }));
      return { factGroups: groups, sourceByFactId: sourceById };
    }, [mapped, sourceByCitationId]);

    if (isLoading) {
      return <LoadingSpinner centered={true} message="Loading extracted facts..." />;
    }

    if (!(selectedJob && mapped)) {
      return (
        <div className={tw`px-2 py-6 text-center font-medium text-content-subtle text-sm`}>
          No extracted facts are available for this patient yet.
        </div>
      );
    }

    const formattedDate = mapped.createdAt
      ? mapped.createdAt.toLocaleDateString('en-US')
      : undefined;

    const badgeFieldsCount = atlasExportEnabled
      ? exportSummary.includedFieldsCount
      : mapped.fieldsTotal;

    return (
      <div className={tw`flex flex-col gap-4 px-2 py-1`}>
        <div className={tw`flex flex-row flex-wrap items-center justify-between gap-2`}>
          <SelectInput
            name="report"
            label="Report:"
            labelPosition="left"
            options={reportOptions}
            initialValue={selectedJob.id}
            onChange={(option) => {
              setSelectedJobId(option?.value);
              telemetry.trackInteraction('atlas_report_switch', {});
            }}
            inputClassName={tw`min-w-70 max-w-96`}
            labelClassName={tw`font-semibold text-content-subtle text-sm`}
          />
          <div className={tw`flex shrink-0 flex-row items-center justify-end gap-2`}>
            {formattedDate && (
              <div className={tw`whitespace-nowrap font-medium text-content-subtle text-xs`}>
                {formattedDate}
              </div>
            )}
            {atlasExportEnabled && exportSummary.excludedFieldsCount > 0 && (
              <div
                className={tw`whitespace-nowrap rounded-sm bg-background-disabled p-1 font-medium text-error-text text-xs`}
              >
                {exportSummary.excludedFieldsCount} excluded
              </div>
            )}
            <div
              className={tw`whitespace-nowrap rounded-sm bg-background-disabled p-1 font-medium text-content-subtle text-xs`}
            >
              {pluralize('field', badgeFieldsCount, true)} in export
            </div>
            {(atlasExportEnabled || atlasExportLoading) && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={atlasExportLoading}
                onClick={
                  atlasExportLoading
                    ? undefined
                    : () =>
                        openExportModal({
                          patientName: patient.displayFull,
                          groups: mapped.groups,
                          excludedIds,
                          citations: selectedJob.attributes.citations,
                          jobId: selectedJob.id,
                          configName: selectedJob.config?.name ?? undefined,
                        })
                }
              >
                <div className={tw`flex items-center space-x-2 whitespace-nowrap font-bold`}>
                  {atlasExportLoading ? (
                    <LoadingSpinner className={tw`text-white`} />
                  ) : (
                    <FontAwesomeIcon icon={faDownload} className={tw`h-4 w-4`} />
                  )}
                  <span>Export</span>
                </div>
              </Button>
            )}
          </div>
        </div>
        <PatientFactExtractionGroupedTable
          factGroups={factGroups}
          showRemoveAction={atlasExportEnabled}
          isRedacted={atlasExportEnabled ? (fact) => isExcluded(fact.id) : undefined}
          onRedactFact={
            atlasExportEnabled
              ? ({ fact }) => {
                  const excluded = !isExcluded(fact.id);
                  toggleExcluded(fact.id);
                  telemetry.trackInteraction('atlas_field_exclude_toggle', {
                    fieldId: fact.id,
                    excluded,
                  });
                }
              : undefined
          }
          onOpenCitation={({ fact }) => {
            const source = sourceByFactId.get(fact.id);
            if (!source) {
              return;
            }
            telemetry.trackInteraction('atlas_source_document_open', {
              fieldId: fact.id,
              fieldName: fact.label,
            });
            if (source.kind === 'document') {
              void openSourceDocumentById(source.binaryId);
            } else {
              openResourceDrawer({ telemetry, model: source.model as never });
            }
          }}
        />
      </div>
    );
  },
});
